import i18n from "@/libs/i18n";
import { computed, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import ability from "@/libs/acl/ability";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  const tableColumns = [
    {
      label: "",
      key: "selected",
      sortable: false,
      thStyle: {
        width: "50px",
      },
    },
  ];

  if (
    ability.can("update", "UserGroupSelectionOption") ||
    ability.can("create", "UserGroupSelectionOption")
  ) {
    tableColumns.push({
      label: "actions.name",
      key: "actions",
      sortable: false,
      thStyle: {
        width: "80px",
      },
    });
  }

  tableColumns.push(
    {
      label: "user_group_selection_option.group",
      key: "name",
      sortable: true,
    },
    {
      label: "user_group_selection_option.bulk.authorized_users",
      key: "users_count",
      sortable: true,
    }
  );

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];

  const searchQuery = ref("");
  const userFilter = ref(null);
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const getUserFilterId = () => {
    const user = userFilter.value;

    if (
      user === null ||
      user === undefined ||
      user === ""
    ) {
      return null;
    }

    if (typeof user !== "object") {
      const id = Number(user);

      return Number.isNaN(id)
        ? null
        : id;
    }

    const id =
      user.id ??
      user.value ??
      null;

    if (
      id === null ||
      id === undefined
    ) {
      return null;
    }

    const numericId = Number(id);

    return Number.isNaN(numericId)
      ? null
      : numericId;
  };

  const currentPageItems = ref([]);
  const selectedGroupIds = ref([]);

  const selectedCount = computed(() => {
    return selectedGroupIds.value.length;
  });

  const normalizeResponse = (response) => {
    let items = [];
    let total = 0;

    if (!response) {
      return {
        items,
        total,
      };
    }

    if (Array.isArray(response.data)) {
      items = response.data;

      total =
        response.meta?.total ??
        response.total ??
        items.length;

      return {
        items,
        total,
      };
    }

    if (
      response.data &&
      Array.isArray(response.data.data)
    ) {
      items = response.data.data;

      total =
        response.data.meta?.total ??
        response.data.total ??
        response.meta?.total ??
        items.length;

      return {
        items,
        total,
      };
    }

    return {
      items,
      total,
    };
  };

  const dataMeta = computed(() => {
    const localItemsCount =
      refListTable.value &&
      Array.isArray(refListTable.value.localItems)
        ? refListTable.value.localItems.length
        : 0;

    return {
      from:
        perPage.value *
          (currentPage.value - 1) +
        (localItemsCount ? 1 : 0),
      to:
        perPage.value *
          (currentPage.value - 1) +
        localItemsCount,
      of:
        totalList.value,
    };
  });

  const currentPageGroupIds = computed(() => {
    if (!Array.isArray(currentPageItems.value)) {
      return [];
    }

    return currentPageItems.value.map(
      (item) => item.id
    );
  });

  const selectedCurrentPageCount = computed(() => {
    return currentPageGroupIds.value.filter(
      (id) =>
        selectedGroupIds.value.includes(id)
    ).length;
  });

  const allCurrentPageSelected = computed(() => {
    return (
      currentPageGroupIds.value.length > 0 &&
      selectedCurrentPageCount.value ===
        currentPageGroupIds.value.length
    );
  });

  const someCurrentPageSelected = computed(() => {
    return (
      selectedCurrentPageCount.value > 0 &&
      !allCurrentPageSelected.value
    );
  });

  const isGroupSelected = (groupId) => {
    return selectedGroupIds.value.includes(
      groupId
    );
  };

  const toggleGroup = (
    groupId,
    checked
  ) => {
    if (checked) {
      if (
        !selectedGroupIds.value.includes(
          groupId
        )
      ) {
        selectedGroupIds.value = [
          ...selectedGroupIds.value,
          groupId,
        ];
      }

      return;
    }

    selectedGroupIds.value =
      selectedGroupIds.value.filter(
        (id) => id !== groupId
      );
  };

  const toggleCurrentPage = (checked) => {
    if (checked) {
      selectedGroupIds.value = [
        ...new Set([
          ...selectedGroupIds.value,
          ...currentPageGroupIds.value,
        ]),
      ];

      return;
    }

    const currentIds = new Set(
      currentPageGroupIds.value
    );

    selectedGroupIds.value =
      selectedGroupIds.value.filter(
        (id) =>
          !currentIds.has(id)
      );
  };

  const clearSelection = () => {
    selectedGroupIds.value = [];
  };

  const fetchList = (
    ctx,
    callback
  ) => {
    store
      .dispatch(
        "user_group_selection_option/getGroupsAdmin",
        {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          user_id: getUserFilterId(),
        }
      )
      .then((response) => {
        const normalized =
          normalizeResponse(response);

        const items =
          normalized.items;

        currentPageItems.value =
          Array.isArray(items)
            ? items
            : [];

        callback(
          currentPageItems.value
        );

        totalList.value =
          normalized.total;
      })
      .catch((error) => {
        console.error(
          "Error loading groups:",
          error
        );

        currentPageItems.value = [];

        callback([]);

        totalList.value = 0;

        toast({
          component:
            ToastificationContent,
          props: {
            title:
              i18n.t("user_group_selection_option.bulk.load_error"),
            icon:
              "AlertTriangleIcon",
            variant:
              "danger",
          },
        });
      });
  };

  const refetchData = () => {
    if (
      refListTable.value &&
      typeof refListTable.value.refresh ===
        "function"
    ) {
      refListTable.value.refresh();
    }
  };

  watch(
    [
      currentPage,
      perPage,
    ],
    () => {
      refetchData();
    }
  );

  watch(
    searchQuery,
    debounce(() => {
      if (
        currentPage.value !== 1
      ) {
        currentPage.value = 1;

        return;
      }

      refetchData();
    }, 500)
  );

  watch(
    [
      sortBy,
      isSortDirDesc,
    ],
    () => {
      refetchData();
    }
  );

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    userFilter,
    getUserFilterId,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    currentPageItems,
    selectedGroupIds,
    selectedCount,
    currentPageGroupIds,
    allCurrentPageSelected,
    someCurrentPageSelected,
    isGroupSelected,
    toggleGroup,
    toggleCurrentPage,
    clearSelection,
  };
}
